import { ArrowsClockwiseIcon } from '@phosphor-icons/react'
import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import classNames from 'classnames'
import { useState } from 'react'
import type { Tool } from '../../utils/api-client/api-schemas'
import { getToolName } from '../../utils/repository-utils.tsx'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import { TooltipWithHover } from '../tooltip'
import * as styles from './sync-latest-dropdown.css.ts'

export type SyncLatestDropdownProps = {
  repositoryId: string
  mappedTools: Tool[]
  onSyncTool: (tool: Tool) => unknown
  triggerClassName?: string
  onOpenChange?: (open: boolean) => unknown
}

export const SyncLatestDropdown: React.FC<SyncLatestDropdownProps> = ({
  mappedTools,
  onSyncTool,
  triggerClassName,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    onOpenChange?.(open)
  }

  if (mappedTools.length === 0) return null

  if (mappedTools.length === 1) {
    const tool = mappedTools[0]!
    return (
      <TooltipWithHover
        trigger={
          <button
            className={classNames(styles.trigger, triggerClassName)}
            aria-label={`Sync tool findings from ${getToolName(tool)}`}
            onClick={() => onSyncTool(tool)}
          >
            <ArrowsClockwiseIcon size={15} weight="light" />
          </button>
        }
        size="small"
      >
        <span>Sync tool findings from {getToolName(tool)}</span>
      </TooltipWithHover>
    )
  }

  return (
    <RadixDropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <TooltipWithHover
        trigger={
          <RadixDropdownMenu.Trigger
            className={classNames(styles.trigger, triggerClassName)}
            aria-label="Sync tool findings"
          >
            <ArrowsClockwiseIcon size={15} weight="light" className={isOpen ? styles.triggerIconActive : undefined} />
          </RadixDropdownMenu.Trigger>
        }
        size="small"
      >
        <span>Sync tool findings</span>
      </TooltipWithHover>

      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content onCloseAutoFocus={e => e.preventDefault()} className={styles.content} sideOffset={4}>
          <div className={styles.header}>Sync latest findings from:</div>
          {mappedTools.map(tool => (
            <RadixDropdownMenu.DropdownMenuItem
              key={tool}
              className={styles.menuItem}
              onSelect={() => onSyncTool(tool)}
            >
              <ToolIcon tool={tool} size="medium" />
              <span className={styles.menuItemLabel}>{getToolName(tool)}</span>
            </RadixDropdownMenu.DropdownMenuItem>
          ))}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}
